/**
 * Step model types — the vocabulary shared by the normaliser, the reducer, the
 * domain interpreters and the store.
 *
 * Pure TypeScript: no React, no zod, no imports from `transport/` beyond the
 * envelope type. `Step` itself deliberately lives in
 * [types/steps.ts](../../types/steps.ts) — it is both the store's model and the
 * renderer's view model, so there is no model→view mapping layer to drift.
 */

import type { StepPhase } from 'src/content/ChatV2/steps/wire/eventType';
import type { WireEnvelope } from 'src/content/ChatV2/transport/eventSchema';
import type {
  Step,
  STEP_KIND,
  StepDetail
} from 'src/content/ChatV2/types/steps';

/** Opaque step identity. See [stepKey](./stepKey.ts) for how it's derived. */
export type StepKey = string;

/**
 * One wire frame, flattened into the two dimensions the reducer works in (`domain` +
 * `phase`) plus the correlation ids, with the payload left intact.
 */
export interface NormalizedEvent {
  raw: WireEnvelope;
  domain: string;
  channel?: 'input' | 'output';
  mode?: string;
  phase: StepPhase;
  phaseRaw: string;

  turnId?: string;
  chatId?: string;
  runId?: string;
  parentRunId?: string;

  data: Record<string, unknown>;

  ordinal: number;
  receivedAt: number;
}

/**
 * The store's immutable snapshot. Handed to React via `useSyncExternalStore`.
 */
export interface StepStoreSnapshot {
  order: readonly StepKey[];
  byKey: ReadonlyMap<StepKey, Step>;
  runIndex: ReadonlyMap<string, StepKey>;
  version: number;
}

export const EMPTY_SNAPSHOT: StepStoreSnapshot = {
  order: [],
  byKey: new Map(),
  runIndex: new Map(),
  version: 0
};

/**
 * One entry of `run.completed.data.additional_steps` — the authoritative settled form,
 * which the backend also persists verbatim onto the message row.
 */
export interface SettledStepEntry {
  type?: string;
  id?: string;
  content?: unknown;
  tool_call_id?: string;
  tool_name?: string;
  status?: string;
  tool_calls?: Array<{
    id?: string;
    name?: string;
    args?: Record<string, unknown>;
    type?: string;
  }>;
  artifact?: { source?: unknown[]; query?: string; lang?: string };
  response_metadata?: Record<string, unknown>;
  usage_metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Cross-entry facts a `settle` implementation needs, gathered by `reconcile`
 * before the fold. Currently just the requesting `ai` entry's tool-call details.
 */
export interface SettleContext {
  input?: Record<string, unknown>;
  toolName?: string;
}

/**
 * Everything domain-specific about a domain, in one object. **The only place a domain
 * name appears in logic.** Registering one is the entire cost of supporting a new
 * backend domain (see `domains/domainRegistry.ts`).
 */
export interface DomainInterpreter {
  readonly domain: string;

  identify?(event: NormalizedEvent): string | undefined;

  kind?(event: NormalizedEvent): STEP_KIND | undefined;

  label?(event: NormalizedEvent): string | undefined;

  apply?(step: Step, event: NormalizedEvent): Step;

  shouldCreate?(event: NormalizedEvent): boolean;

  settle?(entry: SettledStepEntry, ctx?: SettleContext): Partial<Step> | null;

  detail?(step: Step): StepDetail | undefined;
}
